import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import MemoryModal from '../components/MemoryModal';

const { FiPlus, FiImage, FiCalendar, FiTrash2, FiEdit, FiSearch, FiTag, FiUsers, FiX } = FiIcons;

export default function Memories() {
  const { state, dispatch } = useFamilyContext();
  const { memories, familyMembers } = state;
  const [showModal, setShowModal] = useState(false);
  const [editingMemory, setEditingMemory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedPerson, setSelectedPerson] = useState('');
  const [lightboxImage, setLightboxImage] = useState(null);

  // Extract all unique tags
  const allTags = Array.from(new Set(memories.flatMap(m => m.tags ? m.tags.split(',').map(t => t.trim()) : []))).sort();

  const filteredMemories = (memories || []).filter(m => {
      const matchesSearch = m.title.toLowerCase().includes(searchTerm.toLowerCase()) || m.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTag = !selectedTag || (m.tags && m.tags.split(',').map(t => t.trim()).includes(selectedTag));
      const matchesPerson = !selectedPerson || (m.people && m.people.includes(selectedPerson));
      return matchesSearch && matchesTag && matchesPerson;
  }).sort((a, b) => new Date(b.date) - new Date(a.date));

  const handleSave = (memoryData) => {
    if (editingMemory) {
      dispatch({ type: 'UPDATE_MEMORY', payload: { ...editingMemory, ...memoryData } });
      setEditingMemory(null);
    } else {
      dispatch({ type: 'ADD_MEMORY', payload: memoryData });
    }
  };

  const handleDelete = (id) => {
    if (confirm('Erinnerung löschen?')) {
      dispatch({ type: 'DELETE_MEMORY', payload: id });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Familienalbum</h1>
          <p className="text-gray-600 mt-2 dark:text-gray-400">Besondere Momente festhalten</p>
        </div>
        <button
          onClick={() => { setEditingMemory(null); setShowModal(true); }}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 mt-4 sm:mt-0"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Neuer Moment
        </button>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col md:flex-row gap-4 dark:bg-gray-800 dark:border-gray-700">
         <div className="flex-1 relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
            <input 
                type="text" 
                placeholder="Suchen..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
         </div>
         <div className="flex items-center space-x-2">
            <SafeIcon icon={FiTag} className="text-gray-400 w-4 h-4" />
            <select 
                value={selectedTag} 
                onChange={(e) => setSelectedTag(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
                <option value="">Alle Tags</option>
                {allTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
            </select>
         </div>
         <div className="flex items-center space-x-2">
            <SafeIcon icon={FiUsers} className="text-gray-400 w-4 h-4" />
            <select 
                value={selectedPerson} 
                onChange={(e) => setSelectedPerson(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
                <option value="">Alle Personen</option>
                {familyMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
         </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredMemories.map((memory, index) => (
          <motion.div
            key={memory.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col dark:bg-gray-800 dark:border-gray-700"
          >
            <div 
                className="h-48 bg-gray-100 dark:bg-gray-700 flex items-center justify-center relative group cursor-pointer"
                onClick={() => memory.imageUrl && setLightboxImage(memory)}
            >
              {memory.imageUrl ? (
                <img src={memory.imageUrl} alt={memory.title} className="w-full h-full object-cover" />
              ) : (
                <SafeIcon icon={FiImage} className="w-12 h-12 text-gray-300 dark:text-gray-500" />
              )}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 space-x-2">
                 <button onClick={(e) => { e.stopPropagation(); setEditingMemory(memory); setShowModal(true); }} className="p-2 bg-white rounded-full text-gray-800 hover:bg-gray-100">
                    <SafeIcon icon={FiEdit} className="w-4 h-4" />
                 </button>
                 <button onClick={(e) => { e.stopPropagation(); handleDelete(memory.id); }} className="p-2 bg-white rounded-full text-red-600 hover:bg-gray-100">
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                 </button>
              </div>
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-2">
                 <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <SafeIcon icon={FiCalendar} className="w-3 h-3 mr-1" />
                    {format(new Date(memory.date), 'dd. MMMM yyyy', { locale: de })}
                 </div>
                 {memory.people && memory.people.length > 0 && (
                     <div className="flex -space-x-2">
                         {memory.people.map(pid => {
                             const member = familyMembers.find(m => m.id === pid);
                             if (!member) return null;
                             return (
                                 <div key={pid} className="w-6 h-6 rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center text-[10px] text-white font-bold" style={{ backgroundColor: member.color }}>
                                     {member.avatar}
                                 </div>
                             );
                         })}
                     </div>
                 )}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 dark:text-white">{memory.title}</h3>
              <p className="text-sm text-gray-600 line-clamp-3 mb-3 dark:text-gray-400">{memory.description}</p>
              
              {memory.tags && (
                  <div className="flex flex-wrap gap-1 mt-auto">
                      {memory.tags.split(',').map(tag => (
                          <span key={tag} className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full dark:bg-gray-700 dark:text-gray-300">
                              #{tag.trim()}
                          </span>
                      ))}
                  </div>
              )}
            </div>
          </motion.div>
        ))}
        {filteredMemories.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
                {memories.length === 0 ? 'Starten Sie Ihr Album mit dem ersten Eintrag.' : 'Keine Momente gefunden.'}
            </div>
        )}
      </div>

      {showModal && (
        <MemoryModal
          memory={editingMemory}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          familyMembers={familyMembers}
        />
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxImage && (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
                onClick={() => setLightboxImage(null)}
            >
                <div className="relative max-w-4xl max-h-screen w-full flex flex-col items-center">
                    <img 
                        src={lightboxImage.imageUrl} 
                        alt={lightboxImage.title} 
                        className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                    <div className="mt-4 text-white text-center">
                        <h3 className="text-xl font-bold">{lightboxImage.title}</h3>
                        <p className="text-sm opacity-80">{format(new Date(lightboxImage.date), 'dd. MMMM yyyy', { locale: de })}</p>
                    </div>
                    <button 
                        onClick={() => setLightboxImage(null)}
                        className="absolute top-0 right-0 -mt-10 -mr-4 text-white hover:text-gray-300 md:mt-0 md:mr-0 md:top-4 md:right-4 bg-black/50 rounded-full p-2"
                    >
                        <SafeIcon icon={FiX} className="w-8 h-8" />
                    </button>
                </div>
            </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}